import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { SiteNode } from '../types/types';
import { servicesContainer } from '@/core/di/services-container';
import { ConnectorDashboardItem } from '../types/api-types';
import { useOperatorStore } from '@/core/stores/operator-store';

interface EnergyStore {
  // Connector list state (both single-site and operator views)
  siteData: SiteNode[];
  selectedActorId: string | null;
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;

  // View mode and connector rows
  viewMode: 'single' | 'operator';
  connectorRows: ConnectorDashboardItem[];

  // Server-side filter state (operator view)
  filterChargerStatus: string[] | undefined;
  filterMinUtilization: number | undefined;
  filterMaxUtilization: number | undefined;
  filterExcludeInactive: boolean;
  filterOcppConnected: boolean | undefined;

  // Pagination state (operator view)
  currentOperatorId: string | null;
  operatorCurrentPage: number;
  operatorPageSize: number;
  operatorPageSizeOptions: number[];
  operatorFilteredCount: number;
  operatorHasMore: boolean;
  isChangingPage: boolean;
  isLoadingMore: boolean;

  // Actions
  loadConnectors: (operatorId: string, actorId?: string, page?: number, pageSize?: number) => Promise<void>;
  setSelectedActor: (actorId: string | null) => void;
  refreshData: () => Promise<void>;
  clearError: () => void;
  clearData: () => void;

  // Sync filter state without triggering a reload (used to restore persisted uiStore filters on mount)
  setFiltersOnly: (filters: { excludeInactive?: boolean; minUtilization?: number; maxUtilization?: number; chargerStatuses?: string[] }) => void;

  // Server-side filter setters (each triggers reload in operator mode)
  setFilterChargerStatus: (statuses: string[] | undefined) => void;
  setFilterUtilizationRange: (ranges: string[]) => void;
  setFilterHideInactive: (hide: boolean) => void;
  setFilterOcppConnected: (connected: boolean | undefined) => void;

  // Pagination actions
  changeOperatorPage: (page: number) => void;
  changeOperatorPageSize: (pageSize: number) => void;
  loadNextPage: () => Promise<void>;

  // Export
  isExporting: boolean;
  exportAllData: () => Promise<SiteNode[]>;
}

const ALL_UTILIZATION_RANGES = ['0-80', '80-100', '100+'];

export function rangesToUtilization(ranges: string[]): { min?: number; max?: number } {
  // No selection or all selected → no filter
  if (!ranges.length || ALL_UTILIZATION_RANGES.every(r => ranges.includes(r))) return {};
  const mins: number[] = [];
  const maxs: number[] = [];
  const has100Plus = ranges.includes('100+');
  for (const r of ranges) {
    if (r === '0-80')   { mins.push(0);   maxs.push(80); }
    if (r === '80-100') { mins.push(80);  maxs.push(100); }
    if (r === '100+')   { mins.push(100); }
  }
  return {
    min: mins.length ? Math.min(...mins) : undefined,
    // If 100+ is selected, no upper bound
    max: !has100Plus && maxs.length ? Math.max(...maxs) : undefined,
  };
}

export function transformConnectorsToSiteNodes(rows: ConnectorDashboardItem[]): SiteNode[] {
  const siteMap = new Map<number, { siteName: string; connectors: ConnectorDashboardItem[] }>();

  for (const row of rows) {
    if (!siteMap.has(row.actorId)) {
      siteMap.set(row.actorId, { siteName: row.siteName ?? `Site ${row.actorId}`, connectors: [] });
    }
    siteMap.get(row.actorId)!.connectors.push(row);
  }

  return Array.from(siteMap.entries()).map(([actorId, site]) => {
    const machinesInSession = site.connectors.filter(c => c.sessionId != null).length;
    const first = site.connectors[0];

    // Aggregate per-phase actual current across all connectors in this site
    const totalPhase1 = site.connectors.reduce((s, c) => s + (c.actualPhase1Amps ?? 0), 0);
    const totalPhase2 = site.connectors.reduce((s, c) => s + (c.actualPhase2Amps ?? 0), 0);
    const totalPhase3 = site.connectors.reduce((s, c) => s + (c.actualPhase3Amps ?? 0), 0);
    const hasAnyActual = site.connectors.some(c => c.actualPhase1Amps !== null);

    // Total allocated current vs CB limit for the site
    const totalAllocated = site.connectors.reduce((s, c) => s + (c.allocatedCurrentAmps ?? 0), 0);
    const cbLimit = first?.actorCurrentLimitA ?? 0;

    // Aggregate live power kW across all connectors
    const totalLivePowerKw = site.connectors.some(c => c.livePowerKw != null)
      ? site.connectors.reduce((s, c) => s + (c.livePowerKw ?? 0), 0)
      : null;

    const connectorNodes: SiteNode[] = site.connectors.map(c => {
      const phase1 = c.actualPhase1Amps ?? 0;
      const phase2 = c.actualPhase2Amps ?? 0;
      const phase3 = c.actualPhase3Amps ?? 0;
      const hasPhaseData = c.actualPhase1Amps !== null || c.actualPhase2Amps !== null || c.actualPhase3Amps !== null;

      return {
      name: c.chargerName ?? `Machine ${c.machineId}`,
      id: `${actorId}-${c.machineId}-${c.connectorId}`,
      evseId: c.connectorId,
      machineId: c.machineId,
      sessionId: c.sessionId?.toString() ?? null,
      type: 'machine' as const,
      status: (!c.connectorStatusName || c.connectorStatusName === 'Unknown') ? undefined : c.connectorStatusName,
      offered: c.offeredCurrentAmps != null ? c.offeredCurrentAmps.toFixed(1) : undefined,
      actual: hasPhaseData && (phase1 > 0 || phase2 > 0 || phase3 > 0)
        ? `${phase1.toFixed(1)} / ${phase2.toFixed(1)} / ${phase3.toFixed(1)}`
        : c.liveCurrentAmps != null && c.liveCurrentAmps > 0
          ? c.liveCurrentAmps.toFixed(1)
          : undefined,
      actualPhases: hasPhaseData && (phase1 > 0 || phase2 > 0 || phase3 > 0) ? { phase1, phase2, phase3 } : undefined,
      allocated: (c.allocatedCurrentAmps ?? 0) > 0 ? {
        current: c.allocatedCurrentAmps ?? 0,
        total: c.connectorMaxCurrentAmps ?? 0,
        percentage: c.currentUtilizationPercent != null
          ? Math.round(c.currentUtilizationPercent * 10) / 10
          : 0,
      } : undefined,
      currentA: c.liveCurrentAmps ?? null,
      maxCurrentA: c.connectorMaxCurrentAmps ?? null,
      powerKw: c.livePowerKw ?? null,
      maxPowerKw: c.connectorMaxPowerKw ?? null,
      bufferKw: null,
      lastCommunicationAt: c.statusUpdatedAt ?? null,
    };
    });

    return {
      name: site.siteName,
      id: actorId.toString(),
      type: 'location' as const,
      sessions: `${machinesInSession}/${site.connectors.length}`,
      machinesInSession,
      totalMachines: site.connectors.length,
      // Aggregated actual phases for the site row
      actualPhases: hasAnyActual ? { phase1: totalPhase1, phase2: totalPhase2, phase3: totalPhase3 } : undefined,
      actual: hasAnyActual
        ? `${totalPhase1.toFixed(1)} / ${totalPhase2.toFixed(1)} / ${totalPhase3.toFixed(1)}`
        : undefined,
      // CB configuration for the site
      currentA: first?.actorCurrentLimitA ?? null,
      bufferA: first?.actorBufferA ?? null,
      powerKw: (first?.actorPowerLimitKw ?? 0) > 0 ? first!.actorPowerLimitKw! / 1000 : null,
      actualPowerKw: totalLivePowerKw,
      bufferKw: null,
      allocated: {
        current: Math.round(totalAllocated),
        total: cbLimit,
        percentage: cbLimit > 0 ? Math.round((totalAllocated / cbLimit) * 100) : 0,
      },
      children: connectorNodes,
    };
  });
}

export const useEnergyStore = create<EnergyStore>()(
  persist(
    (set, get) => ({
      siteData: [],
      selectedActorId: null,
      isLoading: false,
      error: null,
      lastUpdated: null,

      viewMode: 'single',
      connectorRows: [],

      filterChargerStatus: undefined,
      filterMinUtilization: undefined,
      filterMaxUtilization: undefined,
      filterExcludeInactive: false,
      filterOcppConnected: undefined,

      currentOperatorId: null,
      operatorCurrentPage: 0,
      operatorPageSize: 100,
      operatorPageSizeOptions: [25, 50, 100, 200, 500],
      operatorFilteredCount: 0,
      operatorHasMore: false,
      isChangingPage: false,
      isLoadingMore: false,
      isExporting: false,

      loadConnectors: async (operatorId: string, actorId?: string, page?: number, pageSize?: number) => {
        const state = get();
        const isSingleSite = !!actorId && actorId !== '';
        const currentPage = isSingleSite ? 0 : (page ?? state.operatorCurrentPage);
        const size = isSingleSite ? undefined : (pageSize ?? state.operatorPageSize);
        const isPageChange = !isSingleSite && page !== undefined && page !== state.operatorCurrentPage;
        const rootActorId = isSingleSite ? actorId : operatorId;

        set({ [isPageChange ? 'isChangingPage' : 'isLoading']: true, error: null });

        try {
          const services = servicesContainer.getServices();

          const filterParams = {
            chargerStatuses: state.filterChargerStatus,
            excludeInactive: state.filterExcludeInactive || undefined,
            ocppConnected: state.filterOcppConnected,
            minUtilization: state.filterMinUtilization,
            maxUtilization: state.filterMaxUtilization,
          };

          if (isSingleSite) {
            // Single-site: fetch all connectors for the selected actor, no pagination
            const data = await services.getOperatorMachineListUseCase.execute(rootActorId, {
              ...filterParams,
              limit: undefined,
              offset: 0,
            });

            if (!data.isSuccess) {
              throw new Error(data.errorMessage || 'Failed to load connectors');
            }

            const rows = data.rows ?? [];
            const siteData = transformConnectorsToSiteNodes(rows);

            set({
              connectorRows: rows,
              siteData,
              selectedActorId: actorId,
              viewMode: 'single',
              isLoading: false,
              isChangingPage: false,
              lastUpdated: new Date(),
              error: null,
            });
          } else {
            // Operator view: paginated with count
            const offset = Math.max(0, currentPage * size!);

            const [data, countResult] = await Promise.all([
              services.getOperatorMachineListUseCase.execute(rootActorId, {
                ...filterParams,
                limit: size,
                offset,
              }),
              services.getConnectorCountUseCase.execute(rootActorId, filterParams),
            ]);

            if (!data.isSuccess) {
              throw new Error(data.errorMessage || 'Failed to load connectors');
            }

            const rows = data.rows ?? [];
            const siteData = transformConnectorsToSiteNodes(rows);
            const totalCount = countResult.isSuccess ? countResult.totalCount : rows.length;

            set({
              connectorRows: rows,
              siteData,
              selectedActorId: '',
              viewMode: 'operator',
              currentOperatorId: operatorId,
              operatorCurrentPage: currentPage,
              operatorPageSize: size!,
              operatorFilteredCount: totalCount,
              operatorHasMore: offset + rows.length < totalCount,
              isLoading: false,
              isChangingPage: false,
              isLoadingMore: false,
              lastUpdated: new Date(),
              error: null,
            });
          }
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to load connectors',
            isLoading: false,
            isChangingPage: false,
          });
        }
      },

      setSelectedActor: (actorId: string | null) => {
        set({ selectedActorId: actorId });
        if (!actorId) {
          set({ siteData: [], error: null });
        }
      },

      refreshData: async () => {
        const { selectedActorId, viewMode } = get();
        const selectedOperator = useOperatorStore.getState().selectedOperator;

        if (!selectedOperator?.operatorId) {
          set({ error: 'No operator selected for refresh' });
          return;
        }

        if (viewMode === 'operator' || selectedActorId === '') {
          await get().loadConnectors(selectedOperator.operatorId.toString());
        } else if (selectedActorId) {
          await get().loadConnectors(selectedOperator.operatorId.toString(), selectedActorId);
        }
      },

      clearError: () => set({ error: null }),

      clearData: () => set({
        siteData: [],
        selectedActorId: null,
        error: null,
        lastUpdated: null,
      }),

      setFiltersOnly: ({ excludeInactive, minUtilization, maxUtilization, chargerStatuses }) => {
        set({
          ...(excludeInactive !== undefined && { filterExcludeInactive: excludeInactive }),
          ...(minUtilization !== undefined && { filterMinUtilization: minUtilization }),
          ...(maxUtilization !== undefined && { filterMaxUtilization: maxUtilization }),
          ...(chargerStatuses !== undefined && { filterChargerStatus: chargerStatuses }),
        });
      },

      setFilterChargerStatus: (statuses: string[] | undefined) => {
        set({ filterChargerStatus: statuses, operatorCurrentPage: 0 });
        const op = useOperatorStore.getState().selectedOperator;
        const { selectedActorId, viewMode } = get();
        if (op?.operatorId) get().loadConnectors(op.operatorId.toString(), viewMode === 'single' ? selectedActorId ?? undefined : undefined, 0);
      },

      setFilterUtilizationRange: (ranges: string[]) => {
        const { min, max } = rangesToUtilization(ranges);
        set({ filterMinUtilization: min, filterMaxUtilization: max, operatorCurrentPage: 0 });
        const op = useOperatorStore.getState().selectedOperator;
        const { selectedActorId, viewMode } = get();
        if (op?.operatorId) get().loadConnectors(op.operatorId.toString(), viewMode === 'single' ? selectedActorId ?? undefined : undefined, 0);
      },

      setFilterHideInactive: (hide: boolean) => {
        set({ filterExcludeInactive: hide, operatorCurrentPage: 0 });
        const op = useOperatorStore.getState().selectedOperator;
        const { selectedActorId, viewMode } = get();
        if (op?.operatorId) get().loadConnectors(op.operatorId.toString(), viewMode === 'single' ? selectedActorId ?? undefined : undefined, 0);
      },

      setFilterOcppConnected: (connected: boolean | undefined) => {
        set({ filterOcppConnected: connected, operatorCurrentPage: 0 });
        const op = useOperatorStore.getState().selectedOperator;
        const { selectedActorId, viewMode } = get();
        if (op?.operatorId) get().loadConnectors(op.operatorId.toString(), viewMode === 'single' ? selectedActorId ?? undefined : undefined, 0);
      },

      changeOperatorPage: (page: number) => {
        const op = useOperatorStore.getState().selectedOperator;
        if (op?.operatorId) get().loadConnectors(op.operatorId.toString(), undefined, page);
      },

      changeOperatorPageSize: (pageSize: number) => {
        const op = useOperatorStore.getState().selectedOperator;
        if (op?.operatorId) get().loadConnectors(op.operatorId.toString(), undefined, 0, pageSize);
      },

      loadNextPage: async () => {
        const state = get();
        if (!state.operatorHasMore || state.isLoadingMore || state.isLoading || !state.currentOperatorId) return;

        const nextPage = state.operatorCurrentPage + 1;
        const size = state.operatorPageSize;
        const offset = nextPage * size;

        set({ isLoadingMore: true });

        try {
          const services = servicesContainer.getServices();
          const filterParams = {
            chargerStatuses: state.filterChargerStatus,
            excludeInactive: state.filterExcludeInactive || undefined,
            ocppConnected: state.filterOcppConnected,
            minUtilization: state.filterMinUtilization,
            maxUtilization: state.filterMaxUtilization,
          };

          const data = await services.getOperatorMachineListUseCase.execute(state.currentOperatorId, {
            ...filterParams,
            limit: size,
            offset,
          });

          if (!data.isSuccess) throw new Error(data.errorMessage || 'Failed to load more');

          const newRows = data.rows ?? [];
          const allRows = [...state.connectorRows, ...newRows];
          const allSiteData = transformConnectorsToSiteNodes(allRows);

          set({
            connectorRows: allRows,
            siteData: allSiteData,
            operatorCurrentPage: nextPage,
            operatorHasMore: offset + newRows.length < state.operatorFilteredCount,
            isLoadingMore: false,
          });
        } catch {
          set({ isLoadingMore: false });
        }
      },

      exportAllData: async () => {
        const state = get();
        const selectedOperator = useOperatorStore.getState().selectedOperator;
        if (!selectedOperator?.operatorId) return state.siteData;

        // Single-site mode: all data is already loaded
        if (state.viewMode === 'single') return state.siteData;

        // Operator mode: if all rows are already loaded, export as-is
        const totalCount = state.operatorFilteredCount;
        if (state.connectorRows.length >= totalCount) return state.siteData;

        // Need to fetch all pages
        set({ isExporting: true });
        try {
          const services = servicesContainer.getServices();
          const filterParams = {
            chargerStatuses: state.filterChargerStatus,
            excludeInactive: state.filterExcludeInactive || undefined,
            ocppConnected: state.filterOcppConnected,
            minUtilization: state.filterMinUtilization,
            maxUtilization: state.filterMaxUtilization,
          };

          const pageSize = state.operatorPageSize;
          const allRows: ConnectorDashboardItem[] = [];
          let offset = 0;

          while (offset < totalCount) {
            const data = await services.getOperatorMachineListUseCase.execute(
              selectedOperator.operatorId.toString(),
              { ...filterParams, limit: pageSize, offset }
            );

            if (!data.isSuccess) throw new Error(data.errorMessage || 'Failed to fetch export data');

            const rows = data.rows ?? [];
            if (rows.length === 0) break;

            allRows.push(...rows);
            offset += rows.length;
          }

          set({ isExporting: false });
          return transformConnectorsToSiteNodes(allRows);
        } catch (error) {
          set({ isExporting: false });
          throw error;
        }
      },
    }),
    {
      name: 'energy-storage-v4',
      partialize: (state) => ({
        selectedActorId: state.selectedActorId,
        operatorPageSize: state.operatorPageSize,
      }),
    }
  )
);
